// @ts-nocheck
import { forwardRef, useMemo } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Button,
  ThemeIcon,
  useMantineTheme,
} from '@mantine/core';
import { IconBulb } from '@tabler/icons-react';
import { Paper } from '../../../ui';
import { FadeIn, HoverLift } from '../../../animations';
import { getContrastingTextColor } from '../../../../utils/color';
import type { InsightCardProps } from '../types';

/**
 * InsightCard - Highlighted insight display
 *
 * Displays an important insight or recommendation with actions.
 */
const InsightCard = forwardRef<HTMLDivElement, InsightCardProps>(
  (
    {
      title,
      message,
      severity = 'info',
      icon = <IconBulb size={24} />,
      actions = [],
      variant = 'default',
      gradientVariant = 'primary',
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];

    const severityColors = {
      info: 'blue',
      warning: 'yellow',
      error: 'red',
      success: 'green',
    };

    const color = severityColors[severity];
    const isGradient = variant === 'gradient';

    const gradientBg = `linear-gradient(135deg, ${gradient?.from}, ${gradient?.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    return (
      <FadeIn>
        <HoverLift>
          <Paper
            ref={ref}
            variant={isGradient ? 'gradient' : variant}
            gradientVariant={gradientVariant}
            p="lg"
            className={className}
          >
            <Stack gap="md">
              <Group gap="md" align="flex-start">
                <ThemeIcon
                  size="xl"
                  radius="md"
                  variant={isGradient ? 'transparent' : 'light'}
                  color={isGradient ? undefined : color}
                  style={{
                    background: isGradient ? 'rgba(255,255,255,0.2)' : undefined,
                    color: isGradient ? autoTextColor : undefined,
                  }}
                >
                  {icon}
                </ThemeIcon>

                <Stack gap="xs" style={{ flex: 1 }}>
                  <Text
                    size="lg"
                    fw={600}
                    c={isGradient ? autoTextColor : undefined}
                  >
                    {title}
                  </Text>

                  <Text
                    size="sm"
                    c={isGradient ? autoTextColor : 'dimmed'}
                    style={{ opacity: isGradient ? 0.9 : 1 }}
                  >
                    {message}
                  </Text>
                </Stack>
              </Group>

              {actions.length > 0 && (
                <Group gap="sm">
                  {actions.map((action, idx) => (
                    <Button
                      key={idx}
                      variant={
                        action.variant === 'primary'
                          ? isGradient
                            ? 'white'
                            : 'filled'
                          : action.variant === 'secondary'
                          ? 'light'
                          : 'subtle'
                      }
                      color={isGradient ? 'white' : color}
                      onClick={action.onClick}
                      leftSection={action.icon}
                      style={
                        isGradient && action.variant === 'primary'
                          ? {
                              color: gradient?.from || theme.colors[color][6],
                            }
                          : undefined
                      }
                    >
                      {action.label}
                    </Button>
                  ))}
                </Group>
              )}
            </Stack>
          </Paper>
        </HoverLift>
      </FadeIn>
    );
  }
);

InsightCard.displayName = 'InsightCard';

export default InsightCard;
